import { call, takeLatest, select, put } from 'redux-saga/effects';
import {
  GET_IFRAME_URL,
  SAVE_PAYMENT_METHODS,
  GET_PAYMENT_DETAILS,
  UPDATE_PAYMENT_METHODS,
  SAVE_PAYMENT_METHODS_SUCCESS,
  LIST_PAYMENT_METHODS,
  UPDATE_PAYMENT_METHODS_SUCCESS,
} from '../actions/types';
import { getUserId, getPublicUserId } from '../utilities';
import config, { HTTP, alerts } from './../config';

import {
  getIframeUrlSuccess,
  savePaymentMethodsSuccess,
  getPaymentDetailsSuccess,
  updatePaymentMethodsSuccess,
  setAlertMessage,
  setErrorResponse,
  listPaymentMethodsSuccess,
} from '../actions';
import { getCreditCardUrl, savePaymentMethod, getCpayDetails, updatePaymentMethods, listCpayMethods } from './api';
import { fetchSaga } from './fetchSaga';
import { actions } from '@brighte/brighte-one-core';

const defaultActions = { eError: setErrorResponse, eAlert: setAlertMessage };

const IframeUrlActions = {
  ...defaultActions,
  eResponse: getIframeUrlSuccess,
};

const SavePaymentMethodsActions = {
  ...defaultActions,
  eResponse: savePaymentMethodsSuccess,
};

const GetPaymentIdActions = {
  ...defaultActions,
  eResponse: getPaymentDetailsSuccess,
};

const UpdatePaymentMethodsActions = {
  ...defaultActions,
  eResponse: updatePaymentMethodsSuccess,
};

export const apiIframeUrl = fetchSaga(
  IframeUrlActions,
  function* (): any {
    return yield call(getCreditCardUrl);
  },
  [{ code: HTTP.OK, event: 'success' }],
);

export const apiSavePaymentMethods = fetchSaga(
  SavePaymentMethodsActions,
  function* (payload): any {
    const { token, cardHolder, cardNumber, cardExpiry, cardType } = payload;
    const { identity, initData } = yield select(({ appData: { identity, initData } }) => ({ identity, initData }));

    const userId = getPublicUserId(initData);
    const type = 'card';
    const data = {
      accessToken: identity.accessToken,
      userId,
      type,
      token,
      cardHolder,
      cardNumber,
      cardExpiry,
      cardType,
      source: config.appType,
    };

    const response = yield call(savePaymentMethod, data);
    const { ok } = response;

    if (ok) {
      const { id } = yield response.json();
      yield put(actions.setFieldAttributes('paymentMethod', 'brighteOneOffPayment', { value: id }));
      return { id };
    }

    return response;
  },
  [
    // savePaymentMethod preserves the gateway's real status on failure (see parseGatewayResponse
    // in saga/api.tsx). fetchSaga's switch(status) has no case for 500, so without this a raw 500
    // would fall through to the unmatched-status branch and dispatch savePaymentMethodsSuccess
    // (success) with the failure body — same footgun already fixed for submitApp/listCpayMethods.
    { code: HTTP.INTERNAL_SERVER_ERROR, message: 'Failed to save payment method.', event: 'alert' },
    // fetchSaga's built-in switch treats any 422 as alerts.UNPROCESSABLE_ENTITY ("Card
    // declined..."), which is payment-submission text — misleading here since a 422 from
    // savePaymentMethod means the save itself failed (GraphQL error or missing response data),
    // not a declined card.
    { code: HTTP.UNPROCESSABLE_ENTITY, message: 'Failed to save payment method.', event: 'alert' },
  ],
);

export const apiListPaymentMethods = fetchSaga(
  {
    eAlert: setAlertMessage,
    eError: setErrorResponse,
    eResponse: listPaymentMethodsSuccess,
  },
  function* (): any {
    const {
      initData,
      identity: { accessToken },
    } = yield select(({ appData }) => appData);
    const userId = getPublicUserId(initData);

    return yield call(listCpayMethods, { accessToken, userId });
  },
  [
    // listCpayMethods now preserves the gateway's real status on failure. fetchSaga's
    // switch(status) has no case for 500, so without this a raw 500 would fall through to the
    // unmatched-status branch and dispatch eResponse (success) with the failure body — same
    // footgun documented in form.saga.ts for submitApp.
    { code: HTTP.INTERNAL_SERVER_ERROR, message: 'Failed to retrieve payment methods.', event: 'alert' },
    // fetchSaga's built-in switch treats any 422 as alerts.UNPROCESSABLE_ENTITY ("Card
    // declined..."), which is a payment-submission message — misleading here since a 422 from
    // listCpayMethods means the retrieval itself failed (GraphQL errors or a malformed body), not
    // a declined card.
    { code: HTTP.UNPROCESSABLE_ENTITY, message: 'Failed to retrieve payment methods.', event: 'alert' },
  ],
);

export const apiGetPaymentDetails = fetchSaga(
  GetPaymentIdActions,
  function* (): any {
    const {
      initData,
      identity: { accessToken },
      cpayMethodId,
    } = yield select(({ appData }) => appData);
    const userId = getPublicUserId(initData);

    return yield call(getCpayDetails, { accessToken, cpayMethodId, userId });
  },
  [
    // getCpayDetails preserves the gateway's real status on failure. fetchSaga's switch(status)
    // has no case for 500, so without this a raw 500 would fall through to the unmatched-status
    // branch and dispatch getPaymentDetailsSuccess (success) with the failure body — same
    // footgun already fixed for submitApp/listCpayMethods/savePaymentMethod. The 422 case is
    // also given its own message here since fetchSaga's built-in UNPROCESSABLE_ENTITY alert
    // ("Card declined...") is payment-submission text, not applicable to a failed retrieval.
    { code: HTTP.INTERNAL_SERVER_ERROR, message: 'Failed to retrieve payment method.', event: 'alert' },
    { code: HTTP.UNPROCESSABLE_ENTITY, message: 'Failed to retrieve payment method.', event: 'alert' },
  ],
);

export const apiUpdatePaymentMethods = fetchSaga(
  UpdatePaymentMethodsActions,
  function* (): any {
    const {
      initData,
      identity: { accessToken },
      cpayDetails: { id, cardExpiry },
    } = yield select(({ appData }) => appData);
    const userId = getUserId(initData);

    return yield call(updatePaymentMethods, { accessToken, id, cardExpiry, userId });
  },
  [
    { code: HTTP.NO_CONTENT, event: 'success' },
    { code: HTTP.NOT_FOUND, event: 'alert', message: `Payment method - ${alerts.NOT_FOUND}` },
  ],
);

export function* requestIframeUrl(): Generator<any> {
  yield call(apiIframeUrl);
}

export function* requestSavePaymentMethods(payload: Record<string, any>): Generator<any> {
  yield call(apiSavePaymentMethods, payload);
}

export function* requestListPaymentMethods(): Generator<any> {
  yield put(actions.setFieldAttributes('paymentMethod', 'brighteOneOffPayment', { value: '' }));
  yield put(actions.setFieldAttributes('paymentType', 'brighteOneOffPayment', { value: '' }));
  yield call(apiListPaymentMethods);
}

export function* requestGetPaymentDetails(): Generator<any> {
  yield call(apiGetPaymentDetails);
}

export function* requestUpdatePaymentMethods(): Generator<any> {
  yield call(apiUpdatePaymentMethods);
}

function* watchPaymentActions(): Generator<any> {
  yield takeLatest(GET_IFRAME_URL, requestIframeUrl);
  yield takeLatest(LIST_PAYMENT_METHODS, requestListPaymentMethods);
  yield takeLatest(UPDATE_PAYMENT_METHODS_SUCCESS, requestListPaymentMethods);
  yield takeLatest(SAVE_PAYMENT_METHODS_SUCCESS, requestListPaymentMethods);
  yield takeLatest(SAVE_PAYMENT_METHODS, requestSavePaymentMethods);
  yield takeLatest(GET_PAYMENT_DETAILS, requestGetPaymentDetails);
  yield takeLatest(UPDATE_PAYMENT_METHODS, requestUpdatePaymentMethods);
}

export default watchPaymentActions;
